import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
/** @type {boolean} */
let hasColorGalleryMounted = false;

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => {
      const colorOptionNames = this.#getColorOptionNames();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const shouldAnimate = colorOptionNames.length > 0 && hasColorGalleryMounted && !reduceMotion;

      if (shouldAnimate) {
        this.classList.add('media-gallery--filtering');
      }

      this.#filterMediaByColor({ animate: false }).then(() => {
        if (shouldAnimate) {
          requestAnimationFrame(() => this.classList.remove('media-gallery--filtering'));
        }
      });

      if (colorOptionNames.length > 0) {
        hasColorGalleryMounted = true;
      }
    });
  }

  #controller = new AbortController();

  /** @type {number} */
  #filterTransitionToken = 0;

  /** @type {number} */
  #filterTransitionMs = 80;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduceMotion) {
      this.classList.add('media-gallery--filtering');
      await new Promise((resolve) => setTimeout(resolve, this.#filterTransitionMs));
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {number} ms
   */
  #wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * @returns {string[]}
   */
  #getColorOptionNames() {
    return (this.dataset.colorOptionNames || '')
      .split(',')
      .map((name) => name.trim().toLocaleLowerCase('tr-TR'))
      .filter(Boolean);
  }

  /**
   * @param {string[]} colorOptionNames
   * @returns {string | null}
   */
  #getSelectedColorValue(colorOptionNames) {
    const section = this.closest('.shopify-section');
    const variantPicker = section?.querySelector('variant-picker');
    if (!variantPicker) return null;

    for (const fieldset of variantPicker.querySelectorAll('fieldset[data-option-name]')) {
      const optionName = (fieldset.dataset.optionName || '').trim().toLocaleLowerCase('tr-TR');
      if (!colorOptionNames.includes(optionName)) continue;

      const checked = fieldset.querySelector('input:checked');
      if (checked instanceof HTMLInputElement && checked.value) {
        return checked.value;
      }
    }

    return null;
  }

  /**
   * @param {string} alt
   * @param {string} color
   * @returns {boolean}
   */
  #mediaAltMatchesColor(alt, color) {
    const normalizedAlt = alt.trim().toLocaleLowerCase('tr-TR');
    const normalizedColor = color.trim().toLocaleLowerCase('tr-TR');

    if (!normalizedAlt || !normalizedColor) return false;

    return normalizedAlt === normalizedColor || normalizedAlt.includes(normalizedColor);
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} visible
   */
  #setMediaElementVisible(element, visible) {
    if (visible) {
      element.removeAttribute('hidden');
      element.style.removeProperty('display');
    } else {
      element.setAttribute('hidden', '');
      element.style.display = 'none';
    }
  }

  /**
   * Keeps grid layout correct after color filtering hides items in the DOM.
   * Hidden siblings break :only-child / :first-child CSS, so the first visible
   * item must span the full row when alone or when large-first-image is enabled.
   */
  #updateGridLayoutForVisibleMedia() {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return;

    const allItems = [...grid.querySelectorAll(':scope > li[data-media-alt]')];
    const visibleItems = allItems.filter((item) => !item.hasAttribute('hidden'));
    const hasFilteredItems = visibleItems.length > 0 && visibleItems.length < allItems.length;

    for (const item of allItems) {
      item.classList.remove('media-gallery__grid-item--span-2');
    }

    this.classList.toggle('media-gallery--single-visible', visibleItems.length === 1);

    if (!hasFilteredItems) return;

    if (visibleItems.length === 1) {
      visibleItems[0].classList.add('media-gallery__grid-item--span-2');
    } else if (visibleItems.length > 1 && this.classList.contains('media-gallery--large-first-image')) {
      visibleItems[0].classList.add('media-gallery__grid-item--span-2');
    }
  }

  /**
   * @param {{ animate?: boolean }} [options]
   */
  async #filterMediaByColor({ animate = true } = {}) {
    const colorOptionNames = this.#getColorOptionNames();
    if (!colorOptionNames.length) return;

    const selectedColor = this.#getSelectedColorValue(colorOptionNames);
    if (!selectedColor) return;

    const transitionToken = ++this.#filterTransitionToken;
    const shouldAnimate = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (shouldAnimate) {
      this.classList.add('media-gallery--filtering');
      await this.#wait(this.#filterTransitionMs);
    }

    if (transitionToken !== this.#filterTransitionToken) return;

    /** @type {HTMLElement[]} */
    const filterableElements = [
      ...this.querySelectorAll('slideshow-slide[data-media-alt]'),
      ...this.querySelectorAll('.media-gallery__grid > li[data-media-alt]'),
      ...this.querySelectorAll('.slideshow-controls__thumbnail[data-media-alt]'),
      ...this.querySelectorAll('.dialog-thumbnails-list__thumbnail[data-media-alt]'),
      ...this.querySelectorAll('.dialog-zoomed-gallery > li[data-media-alt]'),
    ];

    if (!filterableElements.length) {
      if (shouldAnimate) this.classList.remove('media-gallery--filtering');
      return;
    }

    let matchCount = 0;

    for (const element of filterableElements) {
      const alt = element.dataset.mediaAlt || '';
      const matches = this.#mediaAltMatchesColor(alt, selectedColor);
      this.#setMediaElementVisible(element, matches);
      if (matches) matchCount++;
    }

    if (matchCount === 0) {
      for (const element of filterableElements) {
        this.#setMediaElementVisible(element, true);
      }
    }

    this.#updateGridLayoutForVisibleMedia();

    if (transitionToken !== this.#filterTransitionToken) return;

    const slideshow = this.slideshow;
    const firstVisibleSlide = this.querySelector('slideshow-slide[data-media-alt]:not([hidden])');

    if (slideshow && firstVisibleSlide) {
      const slides = [...this.querySelectorAll('slideshow-slide')];
      const index = slides.indexOf(firstVisibleSlide);
      if (index >= 0) {
        slideshow.select(index, undefined, { animate: shouldAnimate });
      }
    }

    if (shouldAnimate) {
      await this.#wait(16);
      if (transitionToken !== this.#filterTransitionToken) return;
      this.classList.remove('media-gallery--filtering');
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
